import { Button } from "@/components/ui/button";
import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchSubCategories } from "@/services/getSubCategories";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import { Role } from "@/core/auth/models/role.model";
import withAuthorization from "@/core/auth/components/withAuthorization";
import RoleGuard from "@/core/auth/components/role-guard";
import { updateSubCategory } from "@/services/updateSubCategory";
import {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { deleteSubCategory } from "@/services/deleteSubCategory";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchCategories } from "@/services/getCategories";
import { getCompanies } from "@/services/getCompanies";
import { Link } from "react-router-dom";
import { Edit, Plus, Trash } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

function SubCategoriesTable() {
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [currentSubCategory, setCurrentSubCategory] = React.useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [subCategoryToDelete, setSubCategoryToDelete] =
    React.useState<any>(null);

  const { user } = useAuth();
  const queryClient = useQueryClient();

  const SubCategoryData = useQuery({
    queryKey: ["sub-categories"],
    queryFn: async () => {
      const data = await fetchSubCategories();
      return data.map((subcategory: any) => {
        return {
          ...subcategory,
          company: subcategory.company?.name,
          companyId: subcategory.company?._id,
          categoryName: subcategory.category?.name,
          category: subcategory.category?._id,
        };
      });
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
  });

  const CategoryData = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const data = await fetchCategories();
      return data.map((category: any) => {
        return { ...category, company: category.company?.name };
      });
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
  });

  const CompaniesData = useQuery({
    queryKey: ["companies"],
    queryFn: async () => {
      const data = await getCompanies();
      return data;
    },
    enabled: user?.role === "super-admin",
  });

  const mutation = useMutation({
    mutationFn: (updatedSubCategory: any) => {
      const updatePayload: any = {
        name: updatedSubCategory.name,
        description: updatedSubCategory.description,
        category: updatedSubCategory.category,
      };
      
      if (user?.role === "super-admin" && updatedSubCategory.companyId) {
        updatePayload.company = updatedSubCategory.companyId;
      }
      
      return updateSubCategory(updatedSubCategory._id, updatePayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sub-categories"] });
      toast.success("Sub Category updated successfully");
      setIsEditModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleEditClick = (subcategory: any) => {
    setCurrentSubCategory({
      ...subcategory,
      category: subcategory.category,
      companyId: subcategory.companyId || subcategory.company?._id,
    });
    setIsEditModalOpen(true);
  };

  const deleteMutation = useMutation({
    mutationFn: (subcategoryId: string) => deleteSubCategory(subcategoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sub-categories"] });
      toast.success("Sub Category deleted successfully");
      setIsDeleteModalOpen(false);
    },
    onError: (error) => {
      toast.error("Error deleting sub category: " + error.message);
    },
  });

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentSubCategory) {
      mutation.mutate(currentSubCategory);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentSubCategory((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDeleteClick = (category: any) => {
    setSubCategoryToDelete(category);
    setIsDeleteModalOpen(true);
  };
  return (
    <div className="w-full p-6  mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-8">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Sub Categories</h1>
          <p className="text-muted-foreground">
            Manage and organize your service sub-categories
          </p>
        </div>

        <RoleGuard requiredRoles={[Role.CompanyAdmin, Role.SuperAdmin]}>
          <Link to="/app/add-sub-category">
            <Button variant="default" className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Sub Category
            </Button>
          </Link>
        </RoleGuard>
      </div>

      {/* Main Content */}
      <div className="md:p-4">
        {SubCategoryData.isPending ? (
         [1, 2, 3, 4].map(() => (
              <div className="flex items-center space-x-4 w-full my-8">
                <div className="space-y-2 w-full">
                  <Skeleton className="h-4 w-[100%]" />
                  <Skeleton className="h-4 w-[100%]" />
                </div>
              </div>
            ))
        ) : SubCategoryData.isError ? (
          <div>Error: {SubCategoryData.error.message}</div>
        ) : (
          <>
            <ScrollArea className="rounded-md border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sub Category Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    {user?.role === "super-admin" && (
                      <TableHead>Company</TableHead>
                    )}
                    <TableHead>Date</TableHead>
                    <RoleGuard
                      requiredRoles={[Role.CompanyAdmin, Role.SuperAdmin]}
                    >
                      <TableHead>Actions</TableHead>
                    </RoleGuard>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.isArray(SubCategoryData.data) &&
                  SubCategoryData.data.length > 0 ? (
                    SubCategoryData.data.map((subcategory: any) => (
                      <TableRow key={subcategory._id}>
                        <TableCell>{subcategory.name}</TableCell>
                        <TableCell>{subcategory.description || "-"}</TableCell>
                        <TableCell>{subcategory.categoryName || "-"}</TableCell>
                        {user?.role === "super-admin" && (
                          <TableCell>{subcategory.company || "-"}</TableCell>
                        )}
                        <TableCell>
                          <div className="uppercase">
                            {new Date(subcategory.createdAt).toLocaleString(
                              "en-GM",
                              {
                                year: "numeric",
                                month: "numeric",
                                day: "numeric",
                                hour: "numeric",
                                minute: "numeric",
                                hour12: true,
                              }
                            )}
                          </div>
                        </TableCell>
                        <RoleGuard
                          requiredRoles={[Role.CompanyAdmin, Role.SuperAdmin]}
                        >
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="border-none"
                                      onClick={() =>
                                        handleEditClick(subcategory)
                                      }
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Edit</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="border-none"
                                      onClick={() =>
                                        handleDeleteClick(subcategory)
                                      }
                                    >
                                      <Trash className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Delete</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </TableCell>
                        </RoleGuard>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={user?.role === "super-admin" ? 6 : 5} className="h-24 text-center">
                        No results.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </>
        )}
      </div>

      {isEditModalOpen && (
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogOverlay />
          <DialogContent className="w-96">
            <DialogTitle>Edit Sub Category</DialogTitle>
            <div className="grid grid-cols-1 gap-4 w-full">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={currentSubCategory?.name || ""}
                  onChange={(event) =>
                    setCurrentSubCategory({
                      ...currentSubCategory,
                      name: event.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={currentSubCategory?.description || ""}
                  onChange={(event) =>
                    setCurrentSubCategory({
                      ...currentSubCategory,
                      description: event.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={currentSubCategory?.category || ""}
                  onValueChange={(value) =>
                    setCurrentSubCategory({
                      ...currentSubCategory,
                      category: value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Category">
                      {CategoryData.data?.find(
                        (cat: any) => cat._id === currentSubCategory?.category
                      )?.name || "Select Category"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {CategoryData.data?.map((category: any) => (
                      <SelectItem key={category._id} value={category._id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {user?.role === "super-admin" && (
                <div>
                  <Label htmlFor="company">Company</Label>
                  <Select
                    value={currentSubCategory?.companyId || ""}
                    onValueChange={(value) =>
                      setCurrentSubCategory({
                        ...currentSubCategory,
                        companyId: value,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Company">
                        {CompaniesData.data?.find(
                          (company: any) => company._id === currentSubCategory?.companyId
                        )?.name || "Select Company"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {CompaniesData.data?.map((company: any) => (
                        <SelectItem key={company._id} value={company._id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end w-full gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={() => mutation.mutate(currentSubCategory)}>
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {isDeleteModalOpen && (
        <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
          <DialogOverlay />
          <DialogContent className="w-96">
            <DialogTitle>Delete Sub Category</DialogTitle>
            <div className="space-y-4">
              <p>Are you sure you want to delete this sub category?</p>
              <div className="flex justify-end w-full gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsDeleteModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (subCategoryToDelete) {
                      deleteMutation.mutate(subCategoryToDelete._id);
                    }
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const SubCategoriesTableComponent = withAuthorization(SubCategoriesTable, [
  Role.SuperAdmin,
  Role.CompanyAdmin,
  Role.Employee,
]);

export default SubCategoriesTableComponent;
